import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { ENVIRONMENT } from '../app';
import { TranslateService } from './translate.service';
import { TranslationDomain } from './translation-domain';
import * as Sentry from "@sentry/angular";
import {
  OryFlowValidationError,
  OryFlowReader,
  OryMessageReader,
  hasMessageContext,
  type OryKratosFlowWithUI,
  type OryMessageOptions,
} from '@shopware/iam-ory';
import type { UiText } from '@ory/client';
import { OryMessageContextFormatter } from './ory-message-context.formatter';

/**
 * Domain-aware translation service for Ory flows and errors
 *
 * Handles translation of:
 * - Flow messages (errors, info, success) with domain-specific fallbacks
 * - Flow validation errors
 * - Generic errors
 */
@Injectable({
  providedIn: 'any'
})
export class DomainTranslationService {

  readonly #translateService = inject(TranslateService);
  readonly #translationDomain = inject(TranslationDomain);
  readonly #environment = inject(ENVIRONMENT);
  readonly #contextFormatter = inject(OryMessageContextFormatter);

  /**
   * Translate flow messages directly from an Ory flow
   *
   * @param flow - Ory flow object
   * @param options - Message filtering options (same as OryFlowReader.getMessages)
   * @returns Signal with array of translated messages
   *
   * @example
   * ```typescript
   * // Get ALL messages (errors + info + success)
   * const messages = this.#translationService.translateFlowMessages(flow)();
   *
   * // Get only success/info messages
   * const infoMessages = this.#translationService.translateFlowMessages(flow, {
   *   types: ['success', 'info']
   * })();
   *
   * // Get only password field errors
   * const passwordErrors = this.#translationService.translateFlowMessages(flow, {
   *   types: ['error'],
   *   fields: ['password']
   * })();
   * ```
   */
  translateFlowMessages(
    flow: OryKratosFlowWithUI,
    options?: OryMessageOptions
  ): Signal<string[]> {
    return computed(() => {
      const messages = OryFlowReader.getMessages(flow, options);
      return messages.flatMap((msg: UiText) => this.translateUiTextMessage(msg)());
    });
  }

  /**
   * PRIVATE: Translate a single Ory message (UiText) to a localized string
   *
   * Handles different message types (error, info, success) with appropriate fallbacks.
   * Uses domain-aware translation with fallback to global namespace based on message type.
   *
   * @param oryMessage - Ory UiText message object with id, text, type, and context
   * @returns Signal with array of translated message(s)
   */
  translateUiTextMessage(oryMessage: UiText): Signal<string> {
    const translationKey = OryMessageReader.getMessageKey(oryMessage.id);

    // Format context if:
    // 1. We have a translation key
    // 2. The key has context (type guard narrows to OryMessageKeyWithContext)
    // 3. Context exists on the message
    // 4. Formatter is provided
    const context = translationKey &&
                    hasMessageContext(translationKey) &&
                    oryMessage.context &&
                    this.#contextFormatter
      ? this.#contextFormatter.format(
          translationKey,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          oryMessage.context as any, // Ory types context as generic 'object', we know it's correct at runtime
          this.#translateService.currentLanguage()
        )
      : oryMessage.context;

    if (translationKey) {
      // 1. Try domain-specific translation first
      // For errors: AUTHENTICATION.LOGIN.ERRORS.CREDENTIALS_INVALID
      // For info: AUTHENTICATION.LOGIN.INFO.VERIFICATION_CODE_SENT
      const domainSuffix = oryMessage.type === 'error' ? 'ERRORS' : 'INFO';
      const domainKey = `${this.#translationDomain}.${domainSuffix}.${translationKey}`;
      const domainTranslated = this.#translateService.translateAsSignal(
        domainKey,
        context  // Use formatted context
      );

      // If domain translation exists, use it
      if (domainTranslated() !== domainKey) {
        return domainTranslated;
      }

      // 2. Fallback to global namespace based on message type
      // For errors: ERROR.MESSAGE.CREDENTIALS_INVALID
      // For info/success: INFO.MESSAGE.VERIFICATION_CODE_SENT
      const globalNamespace = oryMessage.type === 'error' ? 'ERROR' : 'INFO';
      const globalKey = `${globalNamespace}.MESSAGE.${translationKey}`;
      const globalTranslated = this.#translateService.translateAsSignal(
        globalKey,
        context  // Use formatted context
      );

      // If global translation exists, use it
      if (globalTranslated() !== globalKey) {
        return globalTranslated;
      }
    }

    // Final fallback to Ory's default English text
    return this.#environment.envName === 'production'
      ? signal(oryMessage.text)
      : signal('MISSING KEY: '+  translationKey + ' - TEXT: ' + oryMessage.text);
  }

  /**
   * Translate error to user-facing message(s)
   *
   * Handles:
   * - OryFlowValidationError: Uses OryMessageReader + TranslationDomain with flexible filtering
   * - Other errors: Returns error message directly
   *
   * @param error - Error object to translate
   * @param options - For OryFlowValidationError, controls which messages to include
   * @returns Signal with array of translated error messages
   *
   * @example
   * ```typescript
   * // Get all errors from flow validation error
   * const errors = this.#translationService.translateError(error)();
   *
   * // Get only specific field errors
   * const passwordErrors = this.#translationService.translateError(error, {
   *   fields: ['password']
   * })();
   * ```
   */
  translateError(
    error: Error,
    options?: Omit<OryMessageOptions, 'types'>
  ): Signal<string[]> {
    // Handle Ory Flow Validation Errors with domain-aware translation
    if (error instanceof OryFlowValidationError) {
      return this.translateFlowMessages(error.flow, {
        ...options,
        types: ['error']  // Only errors for translateError method
      });
    }

    // Handle other error types
    Sentry.addBreadcrumb({
      message: 'Unexpected error in flow',
      level: 'error',
      data: { name: error.name, message: error.message },
    });

    return signal([error.message || 'An unexpected error occurred']);
  }

  translateDomainMessage(key: string, params?: Record<string, unknown>): Signal<{ title: string, message: string }> {
    return computed(() => {
      const title = this.#translateKey(`${this.#translationDomain}.TITLE.${key.toUpperCase()}`, params)();
      const message = this.#translateKey(`${this.#translationDomain}.MESSAGE.${key.toUpperCase()}`, params)();
      return { title, message };
    });
  }

  /**
   * Translate a translation key directly with proper fallback behavior
   *
   * Provides automatic fallback behavior:
   * 1. Try domain-specific key if domain is set (e.g., ERROR.MESSAGE.INVALID_CLIENT)
   * 2. Try global key without domain prefix
   * 3. In production: fallback to UNKNOWN message
   * 4. In development: return the key itself for debugging
   *
   * @param key - Translation key (e.g., 'ERROR.TITLE.INVALID_CLIENT' or 'INVALID_CLIENT')
   * @param params - Optional parameters for interpolation
   * @returns Signal with translated string
   *
   * @example
   * ```typescript
   * // Direct key translation
   * const title = this.translateKey('ERROR.TITLE.INVALID_CLIENT')();
   *
   * // With parameters
   * const message = this.translateKey('ERROR.MESSAGE.PASSWORD_TOO_SHORT', { min_length: 12 })();
   *
   * // With domain fallback (if domain is set)
   * const domainMessage = this.translateKey('INVALID_CLIENT')();
   * // Will try: ERROR.MESSAGE.INVALID_CLIENT -> then fallback
   * ```
   */
  #translateKey(key: string, params?: Record<string, unknown>): Signal<string> {
    // Try the exact key first
    const translated = this.#translateService.translateAsSignal(key, params);

    if (translated() !== key) {
      return translated;
    }

    return signal(key);
  }

  /**
   * Translate error code with domain-aware fallback behavior
   *
   * Automatically tries domain-specific translations first, then falls back to global error messages.
   * Useful for translating error codes from Ory Kratos and Hydra.
   *
   * @param errorCode - Error code to translate (e.g., 'invalid_client', 'session_inactive')
   * @param translationDomain - Optional domain override (defaults to injected domain)
   * @param inputName - Optional input field name for contextualized messages
   * @returns Signal with translated error message
   *
   * @example
   * ```typescript
   * // Simple error code translation
   * const message = this.translateErrorCode('invalid_client')();
   * // -> Tries: ERROR.MESSAGE.INVALID_CLIENT
   *
   * // With domain
   * const domainMessage = this.translateErrorCode('credentials_invalid', 'AUTHENTICATION.LOGIN')();
   * // -> Tries: AUTHENTICATION.LOGIN.ERRORS.CREDENTIALS_INVALID
   * // -> Falls back to: ERROR.MESSAGE.CREDENTIALS_INVALID
   *
   * // With input field context
   * const fieldMessage = this.translateErrorCode('required', undefined, 'email')();
   * // -> Includes field label in translation: "Email is required"
   * ```
   */
  translateErrorCode(errorCode: string, translationDomain?: string, inputName?: string): Signal<string> {
    let inputLabel: undefined | string;
    if (inputName) {
      const prefixField = translationDomain ? `${translationDomain}.FORM.FIELDS` : 'ERROR.FORM.FIELDS';
      const keyField = `${prefixField}.${inputName.toUpperCase()}.LABEL`;

      const labelField = keyField
        ? this.#translateService.translateAsSignal(keyField)
        : signal(inputName ?? '');

      const label = labelField();

      inputLabel = label !== '' && label !== keyField ? label : inputName;
    }

    const prefixError = translationDomain ? `${translationDomain}.ERRORS` : 'ERROR.MESSAGE';
    const keyError = `${prefixError}.${errorCode.toUpperCase()}`;
    const messageError = this.#translateService.translateAsSignal(keyError, { input: inputLabel });
    const fallbackKeyError = `${prefixError}BAD_REQUEST`
    const translated = keyError !== messageError()
      ? messageError
      : this.#environment.envName === 'production' ? this.#translateService.translateAsSignal(fallbackKeyError, { input: inputLabel }) : signal(keyError);

    /**
     * return:
     * 1. translate domain message if available
     * 2. fallback domain message if available
     * 3. fallback default error messages
     * 4. default message if no translation is available
     */
    return fallbackKeyError !== translated()
      ? translated
      : translationDomain
        ? this.translateErrorCode(errorCode, undefined, inputName)
        : this.#environment.envName === 'production' ? this.#translateService.translateAsSignal('ERROR.MESSAGE.UNKNOWN') : signal(keyError);
  }
}

